import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import {
  getProductImage,
} from "../utils/productHelpers";

const SearchResultsWrapper = styled.div`
  position: fixed;
  top: 100px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  border: 1px solid #e5e5e5;
  border-top: none;
  border-radius: 0 0 10px 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  z-index: 10;
  max-height: 600px;
  overflow-y: auto;
  display: ${(props) => (props.isVisible ? "block" : "none")};
  pointer-events: ${(props) => (props.isVisible ? "auto" : "none")};

  @media (max-width: 1200px) {
    width: 95%;
  }

  @media (max-width: 768px) {
    top: 160px;
    width: calc(100% - 20px);
    border-radius: 0 0 8px 8px;
  }

  @media (max-width: 480px) {
    width: 100%;
    top: 140px;
  }
`;

const SearchHeader = styled.div`
  padding: 15px 20px;
  border-bottom: 1px solid #e5e5e5;
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
  font-size: 14px;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  padding: 20px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 15px;
    padding: 15px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    }
`;

const ProductCard = styled(Link)`
  display: block;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
  text-decoration: none;
  color: inherit;
  border: 1px solid #e5e5e5;
  // box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }
`;

const ProductImage = styled.div`
  width: 100%;
  height: 180px;
  background: #ffffffff;
  display: flex;
  align-items: center;
  padding-left: 20px;
  padding-right: 20px;
  justify-content: center;
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background-position: center;
    background-size: contain;
  }

  @media (max-width: 768px) {
    height: 150px;
  }

  @media (max-width: 480px) {
    height: 120px;
  }
`;

const ProductInfo = styled.div`
  padding: 15px;
`;

const ProductTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 8px 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #333;
`;

const ProductPrice = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
`;

const NoResults = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #666;
  font-size: 14px;
`;

const LoadingState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #666;
  font-size: 14px;
`;

const ViewAllButton = styled(Link)`
  display: block;
  margin: 15px 20px 20px;
  padding: 12px 20px;
  background: #333;
  color: white;
  text-align: center;
  text-decoration: none;
  border-radius: 25px;
  font-weight: 600;
  font-size: 13px;
  transition: all 0.2s ease;

  &:hover {
    background: #ff6720;
    transform: translateY(-1px);
  }
`;

const ViewDetailsButton = styled.button`
  width: 100%;
  padding: 8px 16px;
  background: #ff6720;
  color: white;
  border: none;
  border-radius: 25px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e55a1c;
    transform: translateY(-1px);
  }
`;

const ProductDescription = styled.p`
  font-size: 13px;
  color: #666;
  line-height: 1.4;
  margin: 8px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SearchResults = ({
  searchQuery,
  results,
  isLoading,
  isVisible,
  onClose,
  totalResults,
}) => {
  // Add this useEffect to prevent body scroll when search results are visible
  React.useEffect(() => {
    if (isVisible) {
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll
      document.body.style.overflow = 'auto';
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <SearchResultsWrapper isVisible={isVisible}>
      {searchQuery && (
        <SearchHeader>
          {isLoading ? "Searching..." : `Products for ${searchQuery}`}
        </SearchHeader>
      )}

      {isLoading ? (
        <LoadingState>Searching for products...</LoadingState>
      ) : results && results.length > 0 ? (
        <>
          <ProductGrid>
            {results.slice(0, 6).map((product) => (
              <ProductCard
                key={product.id}
                to={`/products/${product.id}`}
                onClick={onClose}
              >
                <ProductImage>
                  <img
                    src={
                      product.images?.[0]?.image_url ||
                      product.image ||
                      getProductImage(product)
                    }
                    alt={product.images?.[0]?.alt_text || product.name}
                    onError={(e) => {
                      e.target.src =
                        "https://via.placeholder.com/250x180?text=No+Image";
                    }}
                  />
                </ProductImage>
                <ProductInfo>
                  <ProductTitle>{product.name}</ProductTitle>
                  <ProductDescription>
                    {product.brief_description ||
                      product.description ||
                      "View product details for more information"}
                  </ProductDescription>
                  <ProductPrice>
                    <ViewDetailsButton>View Details</ViewDetailsButton>
                  </ProductPrice>
                </ProductInfo>
              </ProductCard>
            ))}
          </ProductGrid>

          {results.length > 6 && (
            <ViewAllButton
              to={`/search?q=${encodeURIComponent(searchQuery)}`}
              onClick={onClose}
            >
              See all products ({totalResults || results.length})
            </ViewAllButton>
          )}
        </>
      ) : searchQuery ? (
        <NoResults>
          No products found for "{searchQuery}". Try different keywords.
        </NoResults>
      ) : null}
    </SearchResultsWrapper>
  );
};

export default SearchResults;
